import styled from 'styled-components'
import { useState } from "react";
import {
  Dropdown,
  Menu,
  MenuItem as BaseMenuItem,
  MenuButton as BaseMenuButton,
  Button
} from '@mui/base'
import { Avatar, Badge } from '@mui/material'
import {
  NotificationsNoneOutlined,
  KeyboardArrowDown,
} from '@mui/icons-material'
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import useAuthUser from 'react-auth-kit/hooks/useAuthUser'
import { Brand } from '../../components'
import useSignOut from 'react-auth-kit/hooks/useSignOut'
import { useLocation, useNavigate } from 'react-router-dom'
import { ROUTES } from '../../routes'
import { useConfigContextInfo } from '../../contexts/ConfigContext'
import { PLANS } from '../../utils/planNames'
import { ROLES } from '../../utils/roles'
import { useCountNotification } from '../../hooks'
import { useTranslation } from "react-i18next"
import axiosInstance from '../../services/axiosInstance'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px 44px 0 44px;
  background-color: #fff;
`

const PrimaryContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const SecondaryContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`

const ActionsContainer = styled.div`
  display: flex;
  gap: 24px;
  position: sticky;
  top: 0;
  background-color: #fff;
  z-index: 9998;
  padding: 12px 44px 0 44px;
  box-shadow: 0px 8px 8px rgba(0, 0, 0, 0.05);
`;

const UserContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  background-color: #f1f3f5;
  border-radius: 68px;
`

const UserName = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 16px 0 8px;
`

const MenuButton = styled(BaseMenuButton)`
  background-color: #f1f3f5;
  border: 0;
  border-radius: 68px;
  cursor: pointer;
`

const MenuItem = styled(BaseMenuItem)`
  list-style: none;
  padding: 8px;
  cursor: pointer;
  user-select: none;

  &:last-of-type {
    border-bottom: none;
  }

  &:focus {
    outline: 3px solid #e27837;
    background-color: #e27837;
    color: #f1f3f5;
  }
`

const ActionsSubMenuButton = styled(BaseMenuButton)`
  display: flex;
  background-color: #fff;
  border: 0;
  cursor: pointer;
`

const ActionItem = styled.div`
  padding-bottom: 8px;
  border-bottom: 3px solid
    ${({ isSelected }) => (isSelected ? '#e27837' : '#fff')};
`

const Listbox = styled('ul')(
  ({ theme }) => `
  margin: 8px 0;
  min-width: 200px;
  overflow: auto;
  outline: 0px;
  background: #FFF;
  border: 1px solid #d6d7db;
  color: black;
  box-shadow: 0px 4px 30px #FFF;
  z-index: 1;

  .closed & {
    opacity: 0;
    transform: scale(0.95, 0.8);
    transition: opacity 200ms ease-in, transform 200ms ease-in;
  }

  .open & {
    opacity: 1;
    transform: scale(1, 1);
    transition: opacity 100ms ease-out, transform 100ms cubic-bezier(0.43, 0.29, 0.37, 1.48);
  }

  .placement-top & {
    transform-origin: bottom;
  }

  .placement-bottom & {
    transform-origin: top;
  }
  `,
)

function stringAvatar({ name }) {
  return {
    children: `${name.split(' ')[0][0]}`,
  }
}

const Navbar = () => {
  const auth = useAuthUser()
  const signOut = useSignOut()
  const navigate = useNavigate()
  const location = useLocation()
  const context = useConfigContextInfo()
  const { data } = useCountNotification()
  const { t } = useTranslation('navbar')

  const showActions = () => {
    const NOT_ALLOWED_PATHS = [ROUTES.ONBOARDING, ROUTES.INTAKE]

    return !NOT_ALLOWED_PATHS.includes(location.pathname)
  }

  const isSelected = ({ route }) => {
    return route === location.pathname
  }

  const handleSignOut = async () => {
    await axiosInstance.delete('/logout')
    sessionStorage.removeItem('_distritec_platform_session')
    signOut()
    navigate(ROUTES.LOGIN)
  }

  return (
    <>
      <Container>
        <PrimaryContainer>
          <Brand
            width={200}
            height={56}
            onClick={() =>
              navigate(
                context?.role !== ROLES.EMPLOYEE
                  ? ROUTES.DASHBOARD
                  : ROUTES.USER_DASHBOARD,
              )
            }
          />

          <SecondaryContainer>
            <span onClick={() => navigate(ROUTES.ACCOUNT_CHANGE)} style={{ color: '#666E7B', cursor: 'pointer' }}>
              {t('navbar.account_label', { accountName: context?.accountName })}
            </span>

            <Badge
              color="primary"
              badgeContent={data?.notificationCount || 0}
              max={99}
              onClick={() => navigate(ROUTES.NOTIFICATIONS)}
              style={{ cursor: 'pointer' }}
            >
              <NotificationsNoneOutlined style={{ color: '#858B92' }} />
            </Badge>

            <Dropdown>
              <MenuButton>
                <UserContainer>
                  <Avatar
                    {...stringAvatar({ name: auth.userName })}
                    sx={{ width: 36, height: 36 }}
                  />

                  <UserName>
                    <span style={{ fontSize: 12, textAlign: 'left' }}>
                      {t('navbar.greeting')}
                    </span>
                    <span style={{ fontSize: 14 }}>{auth.userName}</span>
                  </UserName>

                  <KeyboardArrowDown />
                </UserContainer>
              </MenuButton>

              <Menu slots={{ listbox: Listbox }}>
                <MenuItem onClick={() => navigate(ROUTES.NOTIFICATIONS)}>
                  {t('navbar.notifications')}
                </MenuItem>

                {context?.role !== ROLES.EMPLOYEE && (
                  <>
                    <MenuItem onClick={() => navigate(ROUTES.INTERVENTIONS)}>
                      {t('navbar.notes')}
                    </MenuItem>
                    <MenuItem onClick={() => navigate(ROUTES.SYSTEM_EVENTS)}>
                      {t('navbar.events')}
                    </MenuItem>
                  </>
                )}

                <MenuItem onClick={handleSignOut}>
                  {t('navbar.logout')}
                </MenuItem>
              </Menu>
            </Dropdown>
          </SecondaryContainer>
        </PrimaryContainer>
      </Container>

      {showActions() && (
        <ActionsContainer>
          {(context?.role === ROLES.SYSTEM_ADMIN ||
            context?.role === ROLES.ADMINISTRATOR ||
            context?.role === ROLES.SUPERVISOR) && (
            <ActionItem
              isSelected={isSelected({ route: ROUTES.DASHBOARD })}
              onClick={() => navigate(ROUTES.DASHBOARD)}
            >
              <span style={{ fontSize: 18, color: '#666E7B', cursor: 'pointer' }}>
                {t('navbar.actions.team_dashboard')}
              </span>
            </ActionItem>
          )}

          <ActionItem
            isSelected={isSelected({ route: ROUTES.USER_DASHBOARD })}
            onClick={() => navigate(ROUTES.USER_DASHBOARD)}
          >
            <span style={{ fontSize: 18, color: '#666E7B', cursor: 'pointer' }}>
              {t('navbar.actions.my_dashboard')}
            </span>
          </ActionItem>

          <ActionItem
            isSelected={isSelected({ route: ROUTES.PVT })}
            onClick={() => navigate(ROUTES.PVT)}
          >
            <span style={{ fontSize: 18, color: '#666E7B', cursor: 'pointer' }}>
              {t('navbar.actions.pvt')}
            </span>
          </ActionItem>

          <Dropdown>
            <ActionsSubMenuButton>
              <span style={{ fontSize: 18, color: '#666E7B' }}>
                {t('navbar.actions.records')}
              </span>
              <KeyboardArrowDown sx={{ color: '#666E7B' }} />
            </ActionsSubMenuButton>

            <Menu slots={{ listbox: Listbox }}>
              <MenuItem onClick={() => navigate(ROUTES.INTAKE)}>
                {t('navbar.actions.add_daily_log')}
              </MenuItem>
              <MenuItem onClick={() => navigate(ROUTES.SLEEP)}>
                {t('navbar.actions.add_sleep')}
              </MenuItem>
              <MenuItem onClick={() => navigate(ROUTES.CAFFEINE)}>
                {t('navbar.actions.add_caffeine')}
              </MenuItem>
              <MenuItem onClick={() => navigate(ROUTES.INTAKE_LIST)}>
                {t('navbar.actions.edit_logs')}
              </MenuItem>
              <MenuItem onClick={() => navigate(ROUTES.ONBOARDING)}>
                {t('navbar.actions.edit_usual_log')}
              </MenuItem>
            </Menu>
          </Dropdown>

          {(context?.role === ROLES.SYSTEM_ADMIN ||
            context?.role === ROLES.ADMINISTRATOR ||
            context?.role === ROLES.SUPERVISOR) &&
            (context?.plan === PLANS.OPTIMIZED ||
              context?.plan === PLANS.PREDICTION ||
              context?.plan === PLANS.NOW ) && (
              <Dropdown>
                <ActionsSubMenuButton>
                  <span style={{ fontSize: 18, color: '#666E7B' }}>
                    {t('navbar.actions.reports')}
                  </span>
                  <KeyboardArrowDown sx={{ color: '#666E7B' }} />
                </ActionsSubMenuButton>

                <Menu slots={{ listbox: Listbox }}>
                  {context?.plan === PLANS.OPTIMIZED && (
                    <>
                      <MenuItem onClick={() => navigate(ROUTES.OPTIMIZE_REPORT)}>
                        {t('navbar.actions.optimization')}
                      </MenuItem>
                      <MenuItem onClick={() => navigate(ROUTES.SHIFT_FATIGUE)}>
                        {t('navbar.actions.shift_optimization')}
                      </MenuItem>
                    </>
                  )}
                  {(context?.plan === PLANS.OPTIMIZED ||
                  context?.plan === PLANS.PREDICTION) && (
                      <>
                        <MenuItem onClick={() => navigate(ROUTES.PREDICTION)}>
                          {t('navbar.actions.shift_prediction')}
                        </MenuItem>
                        <MenuItem onClick={() => navigate(ROUTES.PREDICTION_HISTORY)}>
                          {t('navbar.actions.prediction_history')}
                        </MenuItem>
                        <MenuItem onClick={() => navigate(ROUTES.FATIGUE_EVOLUTION)}>
                          {t('navbar.actions.period_prediction_history')}
                        </MenuItem>
                        <MenuItem onClick={() => navigate(ROUTES.FATIGUE_DISTRIBUTION)}>
                          {t('navbar.actions.fatigue_distribution')}
                        </MenuItem>
                      </>
                  )}
                  <MenuItem onClick={() => navigate(ROUTES.SLEEP_QUANTITY)}>
                    {t('navbar.actions.sleep_history')}
                  </MenuItem>
                  <MenuItem onClick={() => navigate(ROUTES.REPORT_USER_DATA_QUALITY)}>
                    {t('navbar.actions.daily_log_history')}
                  </MenuItem>
                  <MenuItem onClick={() => navigate(ROUTES.DATA_EXPORT)}>
                    {t('navbar.actions.data_export')}
                  </MenuItem>
                </Menu>
              </Dropdown>
            )}

          {(context?.role === ROLES.SYSTEM_ADMIN ||
            context?.role === ROLES.ADMINISTRATOR) && (
            <Dropdown>
              <ActionsSubMenuButton>
                <span style={{ fontSize: 18, color: '#666E7B' }}>
                  {t('navbar.actions.admin')}
                </span>
                <KeyboardArrowDown sx={{ color: '#666E7B' }} />
              </ActionsSubMenuButton>

              <Menu slots={{ listbox: Listbox }}>
                {context?.role === ROLES.SYSTEM_ADMIN && (
                  <MenuItem onClick={() => navigate(ROUTES.ADMIN_COCKPIT)}>
                    {t('navbar.actions.cockpit')}
                  </MenuItem>
                )}
                <MenuItem onClick={() => navigate(ROUTES.ACCOUNT_CONFIG)}>
                  {t('navbar.actions.settings')}
                </MenuItem>
                <MenuItem onClick={() => navigate(ROUTES.SCHEDULE_IMPORT)}>
                  {t('navbar.actions.shift_import')}
                </MenuItem>
                <MenuItem onClick={() => navigate(ROUTES.USER_IMPORT)}>
                  {t('navbar.actions.user_import')}
                </MenuItem>
                <MenuItem onClick={() => navigate(ROUTES.SHIFT_LIST)}>
                  {t('navbar.actions.shift_list')}
                </MenuItem>
                <MenuItem onClick={() => navigate(ROUTES.USER_FULL_LIST)}>
                  {t('navbar.actions.user_list')}
                </MenuItem>
              </Menu>
            </Dropdown>
          )}
        </ActionsContainer>
      )}
    </>
  )
}

export default Navbar
